<template>
  <div class="sync-results-container">
    <ProgressTracker ref="progressTracker" />
    <template v-if="isCompleted">
      <PanelTemplate
        v-if="syncResult.success.length > 0"
        :color="'success'"
        :icon="markRaw(IconSuccess)">
        {{ i18n.t('messages.syncSuccess', [`${syncResult.success.length}/${entities.length}`]) + ' 🎉' }}
      </PanelTemplate>
      <template v-if="syncResult.failure.length > 0">
        <PanelTemplate
          :color="'danger'"
          :icon="markRaw(IconError)">
          {{ i18n.t('messages.syncFailed', [`${syncResult.failure.length}/${entities.length}`]) }}
        </PanelTemplate>
        <div class="sync-error-table">
          <SyncErrorTable :sync-errors="syncResult.failure" />
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts" generic="T extends IBulkCreateEntity">
  import { AxiosError } from 'axios';
  import { markRaw, onMounted, onUnmounted, Ref, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { IBulkCreateEntity } from '@/common/components/controls/table/bulk-create/BulkCreateEntity';
  import { SpreadSheetModelParser } from '@/common/components/controls/table/bulk-create/model-parser/ModelParser';
  import SyncErrorTable, { ISyncError } from '@/common/components/controls/table/bulk-create/steps/SyncErrorTable.vue';
  import PanelTemplate from '@/common/components/templates/PanelTemplate.vue';
  import ProgressTracker from '@/common/components/ui/ProgressTracker.vue';
  import { IconError, IconSuccess } from '@/common/icons/monochrome';

  const i18n = useI18n();

  interface IProps {
    entities: T[];
    modelParser: SpreadSheetModelParser<T>;
  }

  const { entities, modelParser: modelMapping } = defineProps<IProps>();

  interface IEmits {
    (event: 'sync-completed'): void;
  }

  const emits = defineEmits<IEmits>();

  const isMounted = ref(false);
  const isCompleted: Ref<boolean> = ref(false);
  const progressTracker = ref<InstanceType<typeof ProgressTracker>>();
  const syncResult = ref<{ success: T[]; failure: ISyncError[] }>();

  async function startSync() {
    isCompleted.value = false;
    syncResult.value = { success: [], failure: [] };
    const progressHandler = progressTracker.value.init();
    progressHandler.start(entities.length);
    for (const entity of entities) {
      if (!isMounted.value) {
        return;
      }
      try {
        progressHandler.next(
          `${i18n.t('messages.savingEntity')} ${progressHandler.currentStepID + 1}/${entities.length}`,
        );
        await modelMapping.createEntity(entity);
        syncResult.value.success.push(entity);
      } catch (error) {
        let errorMessage: string;
        if (error instanceof AxiosError || error instanceof Error) {
          errorMessage = error.message;
        } else {
          // Handle other types of errors
          errorMessage = JSON.stringify(error);
        }
        syncResult.value.failure.push({ ...entity, error: errorMessage });
      }
    }
    isCompleted.value = true;
    progressHandler.finish();
  }

  watch(
    isCompleted,
    (value) => {
      if (value) {
        emits('sync-completed');
      }
    },
    { immediate: true },
  );

  onMounted(() => {
    isMounted.value = true;
    startSync();
  });

  onUnmounted(() => {
    isMounted.value = false;
  });
</script>

<style scoped lang="scss">
  .sync-results-container {
    width: 100%;
    min-height: 20rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .sync-error-table {
    display: block;
    width: 100%;
  }
</style>
